import { useMemo } from 'react'
import { Bar, HorizontalBar, Doughnut } from 'react-chartjs-2'
import { Grid, Select, MenuItem } from '@mui/material'
import ChartCard from './ChartCard'
import RowResizer from './RowResizer'
import { usePersistentState } from '../../utils/persist'
import {
  STATUS_COLORS, VENDOR_COLORS, SIDE_COLORS, ROLLOUT_COLORS, CAT_COLOR, TYPE_COLOR, OWNER_COLOR,
  baseBarOptions, stackedBarOptions, doughnutOptions,
} from './palette'
import { barValues, stackTotals, doughnutValues } from './plugins'
import {
  STATUS_VALUES, SIDE_VALUES, ROLLOUT_VALUES, getDisplayStatus, getEntrySides, getEntryOwners,
  getRolloutDecision, countBy, distinctValues,
} from '../../utils/aggregate'

const SORTS = {
  'val-desc':  { label: 'Value ↓', cmp: (a, b) => b[1] - a[1] },
  'val-asc':   { label: 'Value ↑', cmp: (a, b) => a[1] - b[1] },
  'name-asc':  { label: 'Name A–Z', cmp: (a, b) => String(a[0]).localeCompare(String(b[0])) },
  'name-desc': { label: 'Name Z–A', cmp: (a, b) => String(b[0]).localeCompare(String(a[0])) },
}

function SortSelect({ value, onChange }) {
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} variant="standard" disableUnderline
      sx={{ fontSize: 12, color: 'text.secondary', '& .MuiSelect-select': { py: 0.2, pr: 2.5 } }}>
      {Object.entries(SORTS).map(([k, s]) => <MenuItem key={k} value={k} sx={{ fontSize: 12 }}>{s.label}</MenuItem>)}
    </Select>
  )
}

// Todos os gráficos derivam as categorias das linhas JÁ filtradas — não
// aparecem vendors/tipos/owners que não existam no recorte atual.
// A altura é gerida POR LINHA: cada RowResizer redimensiona só a linha acima.
export default function DashboardCharts({ rows }) {
  const [sortVendor, setSortVendor] = usePersistentState('sort:vendor', 'val-desc')
  const [sortType,   setSortType]   = usePersistentState('sort:type', 'val-desc')
  const [sortOwner,  setSortOwner]  = usePersistentState('sort:owner', 'val-desc')

  // Altura por linha (persistente).
  const [hDonuts, setHDonuts] = usePersistentState('row:donuts', 200)
  const [hTeam,   setHTeam]   = usePersistentState('row:team', 240)
  const [hLoad,   setHLoad]   = usePersistentState('row:load', 190)
  const [hType,   setHType]   = usePersistentState('row:type', 210)

  const cats = useMemo(() => ({
    vendors: distinctValues(rows, (r) => r.vendor),
    teams:   distinctValues(rows, (r) => r.teamName),
    techs:   distinctValues(rows, (r) => r.technology),
    types:   distinctValues(rows, (r) => r.feature),
    owners:  distinctValues(rows, getEntryOwners),
  }), [rows])

  const data = useMemo(() => ({
    byStatus:  countBy(rows, getDisplayStatus, STATUS_VALUES),
    bySide:    countBy(rows, getEntrySides, SIDE_VALUES),
    byRollout: countBy(rows, getRolloutDecision, ROLLOUT_VALUES),
    byVendor: countBy(rows, (r) => r.vendor, cats.vendors),
    byTech:   countBy(rows, (r) => r.technology, cats.techs),
    byType:   countBy(rows, (r) => r.feature, cats.types),
    byOwner:  countBy(rows, getEntryOwners, cats.owners),
    teamStacked: STATUS_VALUES.map((st) => ({
      label: st,
      backgroundColor: STATUS_COLORS[st],
      data: cats.teams.map((tm) => rows.filter((r) => r.teamName === tm && getDisplayStatus(r) === st).length),
    })),
  }), [rows, cats])

  const donut = (map) => {
    const entries = Object.entries(map).filter(([, v]) => v > 0)
    return {
      labels: entries.map((e) => e[0]),
      datasets: [{ data: entries.map((e) => e[1]), borderWidth: 0,
        backgroundColor: entries.map(([k], i) => STATUS_COLORS[k] || ROLLOUT_COLORS[k] || VENDOR_COLORS[k] || SIDE_COLORS[i % SIDE_COLORS.length]) }],
    }
  }

  const sortedBar = (map, mode, colorFn) => {
    const entries = Object.entries(map).sort(SORTS[mode].cmp)
    const labels = entries.map((e) => e[0])
    return {
      labels,
      datasets: [{ data: entries.map((e) => e[1]), barPercentage: 0.72, categoryPercentage: 0.72,
        backgroundColor: colorFn ? labels.map(colorFn) : undefined }],
    }
  }

  return (
    <Grid container spacing={2}>
      {/* Linha 1 — donuts */}
      <Grid item xs={12} md={4}>
        <ChartCard title="Status distribution" height={hDonuts}>
          <Doughnut data={donut(data.byStatus)} options={doughnutOptions} plugins={[doughnutValues]} />
        </ChartCard>
      </Grid>
      <Grid item xs={12} md={4}>
        <ChartCard title="By side" subtitle="cell owner" height={hDonuts}>
          <Doughnut data={donut(data.bySide)} options={doughnutOptions} plugins={[doughnutValues]} />
        </ChartCard>
      </Grid>
      {/* Item 22 — Go / No-Go: quantidade de atividades aprovadas (ou não)
          para rollout. Só conta atividades fechadas com decisão registada. */}
      <Grid item xs={12} md={4}>
        <ChartCard title="Go / No-Go" subtitle="closed activities" height={hDonuts}>
          <Doughnut data={donut(data.byRollout)} options={doughnutOptions} plugins={[doughnutValues]} />
        </ChartCard>
      </Grid>
      <Grid item xs={12}><RowResizer value={hDonuts} onChange={setHDonuts} /></Grid>

      {/* Linha 2 — equipas */}
      <Grid item xs={12}>
        <ChartCard title="Activities by team" subtitle="by status" height={hTeam}>
          <Bar data={{ labels: cats.teams, datasets: data.teamStacked }} options={stackedBarOptions} plugins={[stackTotals]} />
        </ChartCard>
      </Grid>
      <Grid item xs={12}><RowResizer value={hTeam} onChange={setHTeam} /></Grid>

      {/* Linha 3 — vendor / tecnologia */}
      <Grid item xs={12} md={6}>
        <ChartCard title="Load by vendor" height={hLoad}
          headerRight={<SortSelect value={sortVendor} onChange={setSortVendor} />}>
          <HorizontalBar
            data={sortedBar(data.byVendor, sortVendor, (k) => VENDOR_COLORS[k] || CAT_COLOR)}
            options={baseBarOptions(true)} plugins={[barValues]} />
        </ChartCard>
      </Grid>
      <Grid item xs={12} md={6}>
        <ChartCard title="Volume by technology" height={hLoad}>
          <Bar
            data={{ labels: cats.techs, datasets: [{ data: cats.techs.map((t) => data.byTech[t]), backgroundColor: TYPE_COLOR, barPercentage: 0.72, categoryPercentage: 0.72 }] }}
            options={baseBarOptions(false)} plugins={[barValues]} />
        </ChartCard>
      </Grid>
      <Grid item xs={12}><RowResizer value={hLoad} onChange={setHLoad} /></Grid>

      {/* Linha 4 — tipo / owner */}
      <Grid item xs={12} md={6}>
        <ChartCard title="By type" subtitle="feature" height={hType}
          headerRight={<SortSelect value={sortType} onChange={setSortType} />}>
          <HorizontalBar
            data={sortedBar(data.byType, sortType, () => '#d85a30')}
            options={baseBarOptions(true)} plugins={[barValues]} />
        </ChartCard>
      </Grid>
      <Grid item xs={12} md={6}>
        <ChartCard title="Workload by activity owner" height={hType}
          headerRight={<SortSelect value={sortOwner} onChange={setSortOwner} />}>
          <HorizontalBar
            data={sortedBar(data.byOwner, sortOwner, () => OWNER_COLOR)}
            options={baseBarOptions(true)} plugins={[barValues]} />
        </ChartCard>
      </Grid>
      <Grid item xs={12}><RowResizer value={hType} onChange={setHType} /></Grid>
    </Grid>
  )
}
